import React from "react";
import { Components } from "react-markdown";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import py from "react-syntax-highlighter/dist/esm/languages/prism/python";
import syntaxTheme from "react-syntax-highlighter/dist/esm/styles/prism/darcula";

SyntaxHighlighter.registerLanguage("python", py);

import styles from "./MarkdownRenderer.module.css";

export const sanitizeOptions = {
  allowedTags: [
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "del",
    "ins",
    "mark",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "a",
    "img",
    "code",
    "pre",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "blockquote",
    "hr",
    "div",
    "span",
  ],
  allowedAttributes: {
    a: ["href", "title", "target", "rel"],
    img: ["src", "alt", "title", "width", "height"],
    code: ["class"],
    pre: ["class"],
    "*": ["class", "id", "style"],
  },
  allowedSchemes: ["http", "https", "mailto", "tel", "data"],
  allowedClasses: { code: ["language-*", "lang-*"], "*": ["hljs*"] },
  disallowedTagsMode: "recursiveEscape",
  allowedIframeHostnames: [],
};

export function getMarkdownComponents(directionClassName: string): Components {
  return {
    a: MarkdownLink,
    code: MarkdownCode,
    blockquote: MarkdownBlockquote,
    ul: MarkdownUl,
    ol: MarkdownOl,
    li: MarkdownLi,
    h1: MarkdownH1,
    h2: MarkdownH2,
    h3: MarkdownH3,
    hr: MarkdownHr,
    p: MarkdownP,
    table: MarkdownTable,
    th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
      <MarkdownTh {...props} directionClassName={directionClassName} />
    ),
    td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
      <MarkdownTd {...props} directionClassName={directionClassName} />
    ),
  };
}

const MarkdownLink: React.FC<JSX.IntrinsicElements["a"]> = ({ href, children, ...props }) => {
  const isExternal = href?.startsWith("http");
  return (
    <a
      href={href}
      className={styles.link}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      {...props}
    >
      {children}
    </a>
  );
};

const MarkdownCode: React.FC<JSX.IntrinsicElements["code"]> = ({ children, className, ...props }) => {
  const isInline = !className?.includes("language-");
  const match = /language-(\w+)/.exec(className || "");
  return !isInline && match ? (
    <SyntaxHighlighter PreTag="div" style={syntaxTheme} language={match[1]}>
      {String(children).replace(/\n$/, "")}
    </SyntaxHighlighter>
  ) : (
    <code className={isInline ? styles["inline-code"] : styles["block-code"]} {...props}>
      {children}
    </code>
  );
};

const MarkdownBlockquote: React.FC<JSX.IntrinsicElements["blockquote"]> = ({ children, ...props }) => (
  <blockquote className={styles.blockquote} {...props}>
    {children}
  </blockquote>
);

const MarkdownUl: React.FC<JSX.IntrinsicElements["ul"]> = ({ children, ...props }) => (
  <ul className={styles["unordered-list"]} {...props}>
    {children}
  </ul>
);

const MarkdownOl: React.FC<JSX.IntrinsicElements["ol"]> = ({ children, ...props }) => (
  <ol className={styles["ordered-list"]} {...props}>
    {children}
  </ol>
);

const MarkdownLi: React.FC<JSX.IntrinsicElements["li"]> = ({ children, ...props }) => (
  <li className={styles["list-item"]} {...props}>
    {children}
  </li>
);

const MarkdownH1: React.FC<JSX.IntrinsicElements["h1"]> = ({ children, ...props }) => (
  <h1 className={styles["heading-1"]} {...props}>
    {children}
  </h1>
);

const MarkdownH2: React.FC<JSX.IntrinsicElements["h2"]> = ({ children, ...props }) => (
  <h2 className={styles["heading-2"]} {...props}>
    {children}
  </h2>
);

const MarkdownH3: React.FC<JSX.IntrinsicElements["h3"]> = ({ children, ...props }) => (
  <h3 className={styles["heading-3"]} {...props}>
    {children}
  </h3>
);

const MarkdownHr: React.FC<JSX.IntrinsicElements["hr"]> = (props) => (
  <hr className={styles.hr} {...props} />
);

const MarkdownP: React.FC<JSX.IntrinsicElements["p"]> = ({ children, ...props }) => (
  <p className={styles.paragraph} {...props}>
    {children}
  </p>
);

const MarkdownTable: React.FC<JSX.IntrinsicElements["table"]> = ({ children, ...props }) => (
  <table className={styles.table} {...props}>
    {children}
  </table>
);

const MarkdownTh: React.FC<JSX.IntrinsicElements["th"] & { directionClassName: string }> = ({
  children,
  directionClassName,
  ...props
}) => (
  <th
    className={styles["table-header"]}
    style={{ textAlign: directionClassName === "ltr" ? "left" : "right" }}
    {...props}
  >
    {children}
  </th>
);

const MarkdownTd: React.FC<JSX.IntrinsicElements["td"] & { directionClassName: string }> = ({
  children,
  directionClassName,
  ...props
}) => (
  <td
    className={styles["table-cell"]}
    style={{ textAlign: directionClassName === "ltr" ? "left" : "right" }}
    {...props}
  >
    {children}
  </td>
);
